export type DateFormat =
  | "dateTime"
  | "dateOnly"
  | "timeOnly"
  | "longDate"
  | "monthYear"
  | "shortMonthDay";

export interface DateFormatOptions {
  format?: DateFormat;
  locale?: string;
  timeZone?: string;
}

const PRESET_OPTIONS: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  dateTime: {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  },
  dateOnly: { year: "numeric", month: "2-digit", day: "2-digit" },
  timeOnly: { hour: "2-digit", minute: "2-digit" },
  longDate: { weekday: "long", year: "numeric", month: "long", day: "numeric" },
  monthYear: { year: "numeric", month: "long" },
  shortMonthDay: { month: "short", day: "numeric" },
};

function resolveDate(date: Date | string | null): Date | null | string {
  if (date === null) return null;
  if (date instanceof Date) return date;
  if (!isNaN(Date.parse(date))) return new Date(date);
  return date; // unparseable — return as-is
}

export function dateFormatter(
  date: Date | string | null | undefined,
  options?: DateFormatOptions,
): string {
  if (date == null) return "";

  const locale = options?.locale || "en-US";
  const tz = options?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const preset = options?.format || "dateTime";

  const resolved = resolveDate(date);
  if (resolved === null) return "";
  if (typeof resolved === "string") return resolved; // unparseable

  const intlOptions: Intl.DateTimeFormatOptions = { ...PRESET_OPTIONS[preset], timeZone: tz };

  return preset === "dateOnly" || preset === "monthYear" || preset === "shortMonthDay"
    ? resolved.toLocaleDateString(locale, intlOptions)
    : resolved.toLocaleString(locale, intlOptions);
}
